import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Zap, Gauge, Printer } from "lucide-react";
import { toast } from "sonner";
import { printTable, num } from "@/lib/printReport";

const fmt = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const today = () => new Date().toISOString().slice(0, 10);

export default function Meters() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [meters, setMeters] = useState([]);
  const [readings, setReadings] = useState([]);
  const [filter, setFilter] = useState("all");
  const [openM, setOpenM] = useState(false);
  const [openR, setOpenR] = useState(false);
  const emptyM = { name: "", location: "", rate_per_kwh: "" };
  const emptyR = { meter_id: "", date: today(), current_reading: "", notes: "" };
  const [fm, setFm] = useState(emptyM);
  const [fr, setFr] = useState(emptyR);

  const load = () => {
    Promise.all([api.get("/fuel/meters"), api.get("/fuel/readings", { params: filter !== "all" ? { meter_id: filter } : {} })])
      .then(([m, r]) => { setMeters(m.data); setReadings(r.data); })
      .catch((e) => toast.error(errMsg(e)));
  };
  useEffect(() => { load(); }, [filter]);

  const submitM = async (e) => {
    e.preventDefault();
    try {
      await api.post("/fuel/meters", { ...fm, rate_per_kwh: parseFloat(fm.rate_per_kwh || 0) });
      toast.success("Meter added"); setOpenM(false); setFm(emptyM); load();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const submitR = async (e) => {
    e.preventDefault();
    if (!fr.meter_id) { toast.error("Select a meter"); return; }
    try {
      await api.post("/fuel/readings", { ...fr, current_reading: parseFloat(fr.current_reading) });
      toast.success("Reading recorded"); setOpenR(false); setFr(emptyR); load();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const del = async (kind, id) => {
    try { await api.delete(`/fuel/${kind}/${id}`); toast.success("Deleted"); load(); } catch (err) { toast.error(errMsg(err)); }
  };

  const totalUnits = readings.reduce((s, r) => s + (r.units || 0), 0);
  const totalCost = readings.reduce((s, r) => s + (r.cost || 0), 0);
  const printReadings = () => printTable({
    title: "Meter Reading Report",
    subtitle: `Meter: ${filter === "all" ? "All Meters" : meters.find((m) => m.id === filter)?.name || ""}`,
    rows: readings,
    columns: [{ key: "date", label: "Date" }, { key: "meter_name", label: "Meter" },
      { label: "Previous", right: true, render: (r) => r.previous_reading ?? "—" }, { label: "Current", right: true, render: (r) => r.current_reading },
      { label: "Units (kWh)", right: true, render: (r) => num(r.units) }, { key: "notes", label: "Notes" }, { key: "created_by", label: "By" },
      { label: "Cost (₹)", right: true, render: (r) => num(r.cost) }],
    totals: [{ label: `Total Units: ${num(totalUnits)} kWh — Total Cost (₹)`, value: num(totalCost) }],
  });

  return (
    <div className="space-y-6 animate-fade-up" data-testid="meters-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">kWh Meters</h1>
          <p className="text-muted-foreground mt-1">Electricity meters and unit readings.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" data-testid="print-readings-btn" onClick={printReadings} className="gap-2 border-border"><Printer className="h-4 w-4" /> Reading Report</Button>
          {isAdmin && (
            <Dialog open={openM} onOpenChange={setOpenM}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="add-meter-btn" className="gap-2 border-border"><Gauge className="h-4 w-4" /> Add Meter</Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border">
                <DialogHeader><DialogTitle>Add Meter</DialogTitle></DialogHeader>
                <form onSubmit={submitM} className="space-y-4">
                  <div className="space-y-2"><Label>Meter Name</Label>
                    <Input data-testid="meter-name-input" required value={fm.name} onChange={(e) => setFm({ ...fm, name: e.target.value })} placeholder="DG SET 750" /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2"><Label>Location</Label>
                      <Input data-testid="meter-location-input" value={fm.location} onChange={(e) => setFm({ ...fm, location: e.target.value })} placeholder="Optional" /></div>
                    <div className="space-y-2"><Label>Rate / kWh (₹)</Label>
                      <Input data-testid="meter-rate-input" type="number" step="0.01" min="0" value={fm.rate_per_kwh} onChange={(e) => setFm({ ...fm, rate_per_kwh: e.target.value })} /></div>
                  </div>
                  <DialogFooter><Button type="submit" data-testid="meter-submit-btn" className="bg-sky-500 hover:bg-sky-600 text-white">Save Meter</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
          <Dialog open={openR} onOpenChange={setOpenR}>
            <DialogTrigger asChild>
              <Button data-testid="add-reading-btn" className="bg-sky-500 hover:bg-sky-600 text-white gap-2"><Plus className="h-4 w-4" /> Record Reading</Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border">
              <DialogHeader><DialogTitle>Record Meter Reading</DialogTitle></DialogHeader>
              <form onSubmit={submitR} className="space-y-4">
                <div className="space-y-2"><Label>Meter</Label>
                  <Select value={fr.meter_id} onValueChange={(v) => setFr({ ...fr, meter_id: v })}>
                    <SelectTrigger data-testid="reading-meter-select"><SelectValue placeholder="Select meter" /></SelectTrigger>
                    <SelectContent>{meters.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}{m.last_reading != null ? ` · last ${m.last_reading}` : ""}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Date</Label>
                    <Input data-testid="reading-date-input" type="date" required value={fr.date} onChange={(e) => setFr({ ...fr, date: e.target.value })} /></div>
                  <div className="space-y-2"><Label>Current Reading (kWh)</Label>
                    <Input data-testid="reading-value-input" type="number" step="0.01" min="0" required value={fr.current_reading} onChange={(e) => setFr({ ...fr, current_reading: e.target.value })} /></div>
                </div>
                <div className="space-y-2"><Label>Notes</Label>
                  <Input data-testid="reading-notes-input" value={fr.notes} onChange={(e) => setFr({ ...fr, notes: e.target.value })} placeholder="Optional" /></div>
                <DialogFooter><Button type="submit" data-testid="reading-submit-btn" className="bg-sky-500 hover:bg-sky-600 text-white">Save Reading</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {meters.map((m) => (
          <Card key={m.id} className="bg-card border-border" data-testid={`meter-card-${m.id}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <p className="font-semibold truncate">{m.name}</p>
                  <p className="text-xs text-muted-foreground">{m.location || "—"} · {fmt(m.rate_per_kwh)}/kWh</p>
                </div>
                {isAdmin && <Button size="icon" variant="ghost" data-testid={`meter-delete-${m.id}`} onClick={() => del("meters", m.id)} className="h-7 w-7 text-rose-400 hover:bg-rose-500/10"><Trash2 className="h-3.5 w-3.5" /></Button>}
              </div>
              <p className="text-2xl font-extrabold text-sky-400 mt-2 font-mono-sku">{m.last_reading ?? "—"}</p>
              <p className="text-xs text-muted-foreground">{m.last_date ? `Last read ${m.last_date}` : "No readings"}</p>
            </CardContent>
          </Card>
        ))}
        {meters.length === 0 && <p className="text-muted-foreground text-sm col-span-full">No meters yet.</p>}
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <div className="space-y-1"><Label className="text-xs">Meter</Label>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-56" data-testid="reading-filter-meter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Meters</SelectItem>{meters.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
          </Select></div>
        <Card className="bg-sky-500/10 border-sky-500/20 ml-auto"><CardContent className="p-4 flex gap-6">
          <div><p className="text-xs uppercase text-muted-foreground font-mono-sku">Units</p><p className="text-xl font-extrabold text-sky-400" data-testid="readings-total-units">{totalUnits.toLocaleString("en-IN")} kWh</p></div>
          <div><p className="text-xs uppercase text-muted-foreground font-mono-sku">Cost</p><p className="text-xl font-extrabold text-rose-400" data-testid="readings-total-cost">{fmt(totalCost)}</p></div>
        </CardContent></Card>
      </div>

      <Card className="bg-card border-border"><CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-border text-left text-muted-foreground">
            <th className="px-4 py-3 font-medium">Date</th><th className="px-4 py-3 font-medium">Meter</th>
            <th className="px-4 py-3 font-medium text-right">Previous</th><th className="px-4 py-3 font-medium text-right">Current</th>
            <th className="px-4 py-3 font-medium text-right">Units</th><th className="px-4 py-3 font-medium text-right">Cost</th>
            <th className="px-4 py-3 font-medium">By</th><th className="px-4 py-3"></th>
          </tr></thead>
          <tbody data-testid="readings-table-body">
            {readings.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-muted-foreground"><Zap className="h-10 w-10 mx-auto mb-2 opacity-40" />No readings recorded.</td></tr>
            ) : readings.map((r) => (
              <tr key={r.id} className="border-b border-border/50 hover:bg-white/5">
                <td className="px-4 py-3 font-mono-sku text-xs">{r.date}</td>
                <td className="px-4 py-3 font-medium">{r.meter_name}</td>
                <td className="px-4 py-3 text-right font-mono-sku text-muted-foreground">{r.previous_reading ?? "—"}</td>
                <td className="px-4 py-3 text-right font-mono-sku">{r.current_reading}</td>
                <td className="px-4 py-3 text-right font-mono-sku text-sky-400 font-semibold">{r.units} kWh</td>
                <td className="px-4 py-3 text-right font-mono-sku">{fmt(r.cost)}</td>
                <td className="px-4 py-3 text-muted-foreground">{r.created_by}</td>
                <td className="px-4 py-3 text-right">{isAdmin && <Button size="icon" variant="ghost" data-testid={`reading-delete-${r.id}`} onClick={() => del("readings", r.id)} className="h-8 w-8 text-rose-400 hover:bg-rose-500/10"><Trash2 className="h-4 w-4" /></Button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent></Card>
    </div>
  );
}
